import has from "lodash/has";
import { cache } from "../../cache";
import { electricalEvaluationMathjs, electricalOnlyUnitConversionEvaluationMathjs, mechanicalEvaluationMathjs, mechanicalOnlyUnitConversionEvaluationMathjs, } from "./Evaluators";
import { createElectricalCustomScope, createMechanicalCustomScope, electricalExpressionScopeGlobals, mechanicalExpressionScopeGlobals, } from "./expression_context/ExpressionContextSystemFunctions";
import { getExpressionDependencies } from "./expressionDependencyExtractor";
import { isExpression, isPlainValue, removeExpressionPrefix } from "./ExpressionEvaluationHelpers";
function checkRequiredDependencies(type, deps, context) {
    let foundDeps = 0;
    let contextGlobals;
    if (type === "mechanical") {
        contextGlobals = mechanicalExpressionScopeGlobals(context);
    }
    else {
        contextGlobals = electricalExpressionScopeGlobals(context);
    }
    // eslint-disable-next-line @buildwithflux/no-array-foreach
    deps?.forEach((dep) => {
        if (has(context, dep) || has(contextGlobals.globals, dep)) {
            foundDeps += 1;
        }
    });
    return deps?.length === foundDeps;
}
export function evaluateElectricalValue(expression, evaluatePlainValues = false, context) {
    return evaluateExpressionOrValue("electrical", expression, evaluatePlainValues, context);
}
export function evaluateMechanicalValue(expression, evaluatePlainValues = false, context) {
    return evaluateExpressionOrValue("mechanical", expression, evaluatePlainValues, context);
}
export const FAST_UNIT_CONVERSIONS = {
    m: 1,
    cm: 0.01,
    mm: 0.001,
    um: 0.000001,
    in: 0.0254,
    inch: 0.0254,
    mil: 0.0000254,
    rad: 1,
    deg: (2 * Math.PI) / 360,
};
const FAST_UNITS_REGEX = Object.keys(FAST_UNIT_CONVERSIONS).join("|");
const NUMBER_REGEX = `[+-]?(?:(?:[0-9]+(?:\\.[0-9]+)?)|Infinity)`;
const FAST_PARSE_REGEX = new RegExp(`^\\s*(${NUMBER_REGEX}) ?(${FAST_UNITS_REGEX})?\\s*$`);
/** For expressions that are simple numbers or numbers-with units like "5mm", "-10.25in", or "3",
 * it is *much* faster to match and parse it directly with a regex than to call mathjs, and even
 * faster than the lru cache we use to memoize the results.
 * @bryhoyt measured the speed in Apr 2025, and found this to be 100x as fast as mathjs and
 * 10x as fast as the cache (at least in a large document where it matters).
 */
function evaluatePlainValueFast(expression) {
    const match = expression.match(FAST_PARSE_REGEX);
    if (match) {
        const [rawValue, origUnit] = [
            match.at(1),
            match.at(-1),
        ];
        const value = parseFloat(rawValue) * (origUnit ? FAST_UNIT_CONVERSIONS[origUnit] : 1);
        return {
            toNumber: (unit) => {
                // Mimic mathjs behaviour
                if (unit) {
                    return value / FAST_UNIT_CONVERSIONS[unit];
                }
                else if (origUnit) {
                    return value / FAST_UNIT_CONVERSIONS[origUnit];
                }
                else {
                    return value;
                }
            },
            toString: () => {
                // Mimic mathjs behaviour
                if (origUnit) {
                    return `${value / FAST_UNIT_CONVERSIONS[origUnit]} ${origUnit}`;
                }
                else {
                    return `${value}`;
                }
            },
            value,
        };
    }
    return undefined;
}
export function evaluateExpressionOrValue(type, expression, evaluatePlainValues = false, context) {
    const isValueAExpression = isExpression(expression);
    if (evaluatePlainValues && !isValueAExpression) {
        // Limit fastEval to mechanical only (for now, at least), because electrical has different
        // available units (conflicting, in some cases), and our rule parsing handles approx zero
        // electrical plain values in a typical project (compared to thousands of mechanical)
        if (type === "mechanical") {
            // Note that if `fastEval` is a match below, that implies it is a (subset of) plain value --
            // meaning we don't have to double-check with the more expensive isPlainValue).
            const fastEval = evaluatePlainValueFast(expression);
            if (fastEval) {
                // The `evaluatePlainValueFast` function is faster than both mathjs and the cache
                // (possibly due to cache key calculation), so if we can, use it here; otherwise
                // fallback to mathjs for more complex expressions. This is important for rule parsing,
                // which runs often, on large numbers of rule defaults, whenever something changes. Rule
                // defaults are mostly very simple expressions, so it makes a real difference.
                return fastEval;
            }
        }
        const isPlain = isPlainValue(expression);
        if (isPlain) {
            return evaluatePlainValue(type, expression);
        }
    }
    if (isValueAExpression) {
        const deps = getExpressionDependencies(type, expression);
        if (!context) {
            if (deps && deps.length > 0) {
                throw new Error("This expression has context dependencies and needs the context to be set");
            }
            return evaluateExpression(type, expression, {});
        }
        const hasRequiredDeps = checkRequiredDependencies(type, deps, context);
        if (hasRequiredDeps) {
            return evaluateExpression(type, expression, context);
        }
        // The provided expression context did not satisfy the expressions dependencies
        // We will skip evaluation and not throw a error here because the user might have just typed invalid variable
        return expression;
    }
    return expression;
}
export function doesMechanicalExpressionHaveSyntaxError(expr) {
    try {
        mechanicalEvaluationMathjs.evaluate(removeExpressionPrefix(expr), {});
    }
    catch (err) {
        if (err instanceof Error && err.name === "SyntaxError") {
            return true;
        }
    }
    return false;
}
function evaluateExpression(type, expression, context) {
    return cache(() => {
        let scope;
        if (type === "mechanical") {
            scope = createMechanicalCustomScope(context);
        }
        else {
            scope = createElectricalCustomScope(context);
        }
        const expressionWithoutEquals = removeExpressionPrefix(expression);
        try {
            const evaluator = {
                mechanical: mechanicalEvaluationMathjs,
                electrical: electricalEvaluationMathjs,
            }[type];
            return evaluator.evaluate(expressionWithoutEquals.toString(), scope);
        }
        catch {
            return expression;
        }
    }, "evaluateExpression", [type, expression, context]);
}
function evaluatePlainValue(type, expression) {
    return cache(() => {
        const evaluator = {
            mechanical: mechanicalOnlyUnitConversionEvaluationMathjs,
            electrical: electricalOnlyUnitConversionEvaluationMathjs,
        }[type];
        try {
            return evaluator.evaluate(expression.toString());
        }
        catch {
            return expression;
        }
    }, "evaluatePlainValue", [type, expression]);
}
//# sourceMappingURL=EvaluateExpression.js.map